import { EmptyData } from '@components/EmptyData/EmptyData';
import {
  Table,
  TableBody,
  TableCell,
  TableFoot,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@atgfr8/atg-ui-tailwind';
import { For, JSXElement, mergeProps, Show } from 'solid-js';

import DragDropTable, { CustomTableCell } from './DragAndDropTable';

export type Column<T> = {
  name: string | JSXElement;
  key?: keyof T | string;
  renderCell?: (arg: T, index: number) => string | JSXElement;
  minWidth?: number | string;
  maxWidth?: number | string;
  textAlign?:
    | 'start'
    | 'end'
    | 'left'
    | 'right'
    | 'center'
    | 'justify'
    | 'match-parent';
  cellColor?: string;
  headerClasses?: string;
};

export type FooterType<T> = {
  key: keyof T | string;
  renderCell?: (arg?: T) => string | JSXElement;
  textAlign?:
    | 'start'
    | 'end'
    | 'left'
    | 'right'
    | 'center'
    | 'justify'
    | 'match-parent';
  minWidth?: number;
  maxWidth?: number;
};

interface Props<T> {
  columns?: Column<T>[];
  rows?: T[];
  footerRow?: FooterType<T>[];
  footerBorder?: boolean;
  getRowClass?: (row: T, index: number, allRows: T[]) => string;
  hideHeader?: boolean;
  onRowClick?: (rowData: T) => void;
  children?: JSXElement;
  tableBodyClasses?: string;
  tableFooterClasses?: string;
  tableBodyRowClasses?: string;
  tableFooterRowClasses?: string;
  dragDropEnable?: boolean;
  onDragEnd?: (updatedItems: T[]) => void;
  columnNoWrap?: boolean;
  cellClasses?: string;
  noEmptyRender?: boolean;
  tableHeaderClasses?: string;
  tableContainerClasses?: string;
  tableEvenRowClasses?: string;
  imgClass?: string;
}

export const BasicTable = <T,>(props: Props<T>) => {
  props = mergeProps(
    {
      tableBodyClasses: 'w-full',
      dragDropEnable: false,
    },
    props,
  );

  const headers = (array: Column<T>[]) => (
    <For each={array}>
      {(item) => (
        <TableHeaderCell
          class={`text-sm font-medium text-black ${props.cellClasses} ${item.headerClasses}`}
          style={{
            'text-wrap': Boolean(props.columnNoWrap) ? 'nowrap' : 'wrap',
            ...(item.minWidth && {
              'min-width':
                typeof item.minWidth === 'number'
                  ? `${item.minWidth}px`
                  : item.minWidth,
            }),
            ...(item.maxWidth && {
              'max-width':
                typeof item.maxWidth === 'number'
                  ? `${item.maxWidth}px`
                  : item.maxWidth,
            }),
          }}
        >
          {item.name}
        </TableHeaderCell>
      )}
    </For>
  );

  const footers = (footerArray: FooterType<T>[]) => (
    <For each={footerArray}>
      {(item) => (
        <TableCell class={`text-sm font-bold text-black ${props.cellClasses}`}>
          {item.renderCell ? item.renderCell(item as T) : ''}
        </TableCell>
      )}
    </For>
  );

  const hasOperationType = (item: T) => {
    return typeof item === 'object' && item !== null && 'operationType' in item;
  };

  return (
    <div class={props.tableContainerClasses}>
      <Table>
        {!Boolean(props.hideHeader) && (
          <TableHead class={`head ${props.tableHeaderClasses}`}>
            <TableRow>{headers(props.columns ?? [])}</TableRow>
          </TableHead>
        )}
        {Boolean(props.children) ? (
          <TableBody>{props.children}</TableBody>
        ) : (
          <>
            <Show
              when={props.rows?.length === 0 && !Boolean(props.noEmptyRender)}
            >
              <TableBody class={props.tableBodyClasses}>
                <TableRow>
                  <TableCell
                    colSpan={props.columns?.length}
                    style={{ border: '0' }}
                  >
                    <EmptyData imgClass={props.imgClass} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Show>
            <Show when={props.rows && props.rows.length > 0}>
              <TableBody class={props.tableBodyClasses}>
                {Boolean(props.dragDropEnable) ? (
                  <DragDropTable<T>
                    rows={props.rows || []}
                    columns={props.columns || []}
                    onRowClick={props.onRowClick}
                    tableBodyRowClasses={props.tableBodyRowClasses!}
                    onDragEnd={props.onDragEnd!}
                    cellClasses={props.cellClasses}
                  />
                ) : (
                  <For each={props.rows}>
                    {(rowItem, index) => (
                      <TableRow
                        class={`${props.tableBodyRowClasses} ${
                          index() % 2 == 0 ? props.tableEvenRowClasses : ''
                        } ${
                          props.getRowClass?.(
                            rowItem,
                            index(),
                            props.rows ?? [],
                          ) ?? ''
                        }
`}
                        onClick={() =>
                          props.onRowClick && props.onRowClick(rowItem)
                        }
                        style={{
                          visibility:
                            hasOperationType(rowItem) &&
                            rowItem.operationType === 'Delete'
                              ? 'hidden'
                              : 'visible',
                        }}
                      >
                        <For each={props.columns}>
                          {(columnItem) => (
                            <CustomTableCell
                              columnItem={columnItem}
                              rowItem={rowItem}
                              index={index()}
                              cellClasses={props.cellClasses}
                            />
                          )}
                        </For>
                      </TableRow>
                    )}
                  </For>
                )}
              </TableBody>
            </Show>
          </>
        )}
        {props.footerRow && (
          <TableFoot class={props.tableFooterClasses}>
            <TableRow class={props.tableFooterRowClasses}>
              {footers(props.footerRow)}
            </TableRow>
          </TableFoot>
        )}
      </Table>
    </div>
  );
};
